import * as React from 'react';
import styled from 'styled-components';
import { ITagInputProps } from '../tag-input-types';
import { Icon } from '../../icon';

interface ITagItemInputFieldProps extends Pick<ITagInputProps, 'disabled'> {
    inputFieldValue?: string;
    showAddButton?: boolean;
    autoFocus?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    rootElementWidth: number;
    onKeyDown(ev: React.KeyboardEvent<HTMLInputElement>): void;
    onChange(ev: React.ChangeEvent<HTMLInputElement>): void;
    onAddItem?(): void;
    onBlur(ev: React.FocusEvent): void;
    onClick?(ev: React.FocusEvent): void;
}

function getSpanWidth(element: HTMLSpanElement) {
    return Math.ceil(element.getBoundingClientRect().width) + 8;
}

const AddIconButton = styled(Icon).attrs({
    className: String('ap-tagInput-addIcon'),
    tabIndex: -1,
    iconName: 'ap-add-round.solid'
})`
    cursor: pointer;
    fill: ${({ theme: { tagInput: { colors } } }) => colors.addButtonColor};
`;

const HiddenField = styled.span.attrs(() => ({
    className: String('ap-tagInput-hiddenField')
}))`
    opacity: 0;
    position: absolute;
`;

const TagItemInputField = styled.input.attrs(({ width }) => ({
    className: String('ap-tagInput-inputField'),
    tabIndex: -1,
    style: { width: `${width}px` }
})) <ITagItemInputFieldProps>`
    border: none;
    max-width: ${({ width, rootElementWidth }) => ((width! < rootElementWidth! - 46) ? '100%' : 'calc(100% - 30px)')};
    position: relative;
    background-color: transparent;
    z-index: 1;
    font-size: ${({ theme: { typography: { small } } }) => small.fontSize};
    &:focus {
        outline: none;
    }
`;

export const TagInputField = React.memo((props: ITagItemInputFieldProps) => {
    const {
        inputFieldValue, showAddButton, inputRef,
        disabled, onAddItem, rootElementWidth, autoFocus
    } = props;

    const spanElementRef = React.useRef<HTMLSpanElement>(null);
    const [hiddenFieldWidth, setWidth] = React.useState(5);

    React.useEffect(() => {
        const width = (inputFieldValue && spanElementRef.current) ? getSpanWidth(spanElementRef.current) : 5;
        if (width !== hiddenFieldWidth) {
            setWidth(width);
        }
    }, [hiddenFieldWidth, inputFieldValue]);

    return (
        <React.Fragment>
            <HiddenField ref={spanElementRef}>
                {inputFieldValue}
            </HiddenField>
            <TagItemInputField
                value={inputFieldValue}
                ref={inputRef}
                width={hiddenFieldWidth}
                rootElementWidth={rootElementWidth}
                disabled={disabled}
                onKeyDown={props.onKeyDown}
                onChange={props.onChange}
                onBlur={props.onBlur}
                autoFocus={autoFocus}
                autoComplete="off" />
            {showAddButton
                ? (
                    <AddIconButton onClick={onAddItem} />
                )
                : null}
        </React.Fragment>
    );
});

TagInputField.displayName = 'TagInputField';
